import { useUserStore } from '@/store/auth/store';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { get } from 'lodash';
import i18next from 'i18next';

// Map language codes to Accept-Language header values
const LANGUAGE_HEADER_MAP: Record<string, string> = {
  'en': 'en-US',
  'fr': 'fr',
};

const getHeaderLanguage = (lang: string): string => {
  return LANGUAGE_HEADER_MAP[lang] || lang;
};

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_DATA_PLATFORM_API_URL,
  timeout: Number(import.meta.env.VITE_DATA_PLATFORM_API_TIMEOUT),
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': getHeaderLanguage(i18next.language || 'en'),
  },
});

i18next.on('languageChanged', (lng) => {
  axiosInstance.defaults.headers.common['Accept-Language'] = getHeaderLanguage(lng);
  if (typeof window !== 'undefined') {
    localStorage.setItem('i18nextLng', lng);
  }
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = Cookies.get('accessToken') || useUserStore.getState().accessToken;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    const currentLang = i18next.language ||
      (typeof window !== 'undefined' ? localStorage.getItem('i18nextLng') : null) ||
      'en';
    config.headers['Accept-Language'] = getHeaderLanguage(currentLang);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing to avoid multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// Response interceptor to handle specific errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (response) {
      if (response.status === 401) {
        // Try to refresh token before logging out
        if (!isRefreshing) {
          isRefreshing = true;
          
          try {
            const { refreshAccessToken } = await import('@/core/utils/auth0');
            const newToken = await refreshAccessToken();
            
            // Update the failed request with new token
            config.headers['Authorization'] = `Bearer ${newToken}`;
            
            // Process queued requests
            processQueue(null, newToken);
            isRefreshing = false;
            
            // Retry the original request
            return axiosInstance(config);
          } catch (refreshError) {
            // Token refresh failed, logout user
            processQueue(refreshError, null);
            isRefreshing = false;
            
            useUserStore.getState().logout();
            toast.error('Your session has expired. Please login again.');
            return Promise.reject(refreshError);
          }
        }
        
        // Queue this request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          config.headers['Authorization'] = `Bearer ${token}`;
          return axiosInstance(config);
        }).catch(err => {
          return Promise.reject(err);
        });
      } else {
      // Don't show toast for DCA-related endpoint errors
      const isDcaError = (response.status === 404 || response.status === 400) && 
        (config.url?.includes('dca-strategies') || config.url?.includes('dca-models'));
        
      if (!isDcaError) {
        toast.error(get(response, 'data.message', 'An error occurred. Please try again.'));
      }
      }
    } else {
      // Handle network or unknown errors
      toast.error('Network error. Please check your internet connection.');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
